import React from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { ReactComponent as CloseIcon } from '../../assets/icons/close-circle.svg';
import { ReactComponent as ExpandIcon } from '../../assets/icons/tables/expand-icon.svg';
import { ReactComponent as NewTabIcon } from '../../assets/icons/tables/new-tab.svg';

interface PropertiesInterface {
  open: boolean;
  title?: string;
  topLeftContent?: React.ReactNode;
  content: JSX.Element;
  position?: 'right' | 'left';
  closePanel: () => void;
  url?: string;
  urlNewTab?: string;
  hideHeader?: boolean;
  preventQueryClear?: boolean;
  width?: string | number;
}

/**
 * Custom Side Panel function component
 * @param {any} properties - The properties
 * @returns {any} JSX.Element
 */
function SidePaneModel(properties: PropertiesInterface) {
  const { t, i18n } = useTranslation('sidePanel');
  const {
    title,
    topLeftContent,
    open,
    position,
    content,
    closePanel,
    url,
    urlNewTab,
    hideHeader,
    preventQueryClear,
    width,
  } = properties;
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Handle close panel function
   */
  const handleClosePanel = () => {
    if (!preventQueryClear) {
      navigate(location.pathname, { replace: true });
    }
    closePanel();
  };

  /**
   * Handle expand panel function
   */
  const handleExpand = () => {
    handleClosePanel();
    if (url) {
      navigate(url);
    }
  };

  /**
   * Handle new tab function
   */
  const handleNewTab = () => {
    handleClosePanel();
    if (urlNewTab) {
      window.open(urlNewTab, '_blank');
    } else {
      window.open(url, '_blank');
    }
  };

  /**
   * Render side panel header left content
   * @returns {React.ReactNode} The rendered header element
   */
  const renderHeaderLeftContent = (): React.ReactNode => {
    if (topLeftContent) {
      return topLeftContent;
    }

    if (title && title.length > 0) {
      return (
        <div className="header-title">
          <p>{t(title)}</p>
        </div>
      );
    }

    return (
      <div className="header-icons">
        <div className="button-wrap" style={{ cursor: 'pointer' }}>
          <span style={{ display: 'flex' }}>
            <ExpandIcon onClick={handleExpand} className="icon" />
          </span>
          <div className="hover-label">{t('expandPages', 'Expand Pages')}</div>
        </div>
        <div className="button-wrap" style={{ cursor: 'pointer' }}>
          <span style={{ display: 'flex' }}>
            <NewTabIcon onClick={handleNewTab} className="icon" />
          </span>
          <div className="hover-label">{t('expandNewPages', 'Expand New Pages')}</div>
        </div>
      </div>
    );
  };

  const customStyle: React.CSSProperties | undefined = width
    ? { width: typeof width === 'number' ? `${width}px` : width }
    : undefined;

  return open
    ? ReactDOM.createPortal(
        <div
          className={`custom-side-panel-wrap ${position} ${i18n.language === 'ar' ? 'rtl' : ''}`}
          style={customStyle}>
          {!hideHeader && (
            <div className="custom-side-panel-header">
              {renderHeaderLeftContent()}
              <CloseIcon className="icon" onClick={handleClosePanel} />
            </div>
          )}
          <div className="custom-side-panel-content">{content}</div>
        </div>,
        document.body,
      )
    : null;
}
export default SidePaneModel;

SidePaneModel.defaultProps = {
  title: '',
  position: 'right',
};
